import {
  ChipField,
  CreateButton,
  FormDataConsumer,
  FunctionField,
  Labeled,
  NumberInput,
  ReferenceManyField,
  SingleFieldList,
  TabbedForm,
  TabbedFormViewProps,
  TextInput,
  maxLength,
  required,
  useCanAccess,
} from 'react-admin';
import { Box, Divider, Grid2 as Grid } from '@mui/material';
import { MerchantResource } from '../../../Entities/Merchant';
import { RegistrationResource } from '../../../Entities/Registration';
import { AffiliateNetworkResource } from '../../../Entities/AffiliateNetwork';
import { BILLING_TYPE_CHOICES } from '../utils';
import { ReactElement } from 'react';
import JsonApiHasOneReferenceInput from '../../../Components/JsonApiHasOneReferenceInput';
import ImageUrlInput from '../../../Components/ImageUrlInput';

export default function SummaryTab(props: TabbedFormViewProps): ReactElement {
  return (
    <TabbedForm.Tab label="Summary" {...props}>
      <SummaryTabContent />
    </TabbedForm.Tab>
  );
}

function SummaryTabContent(): ReactElement {
  const affiliateNetworkAccess = useCanAccess({ resource: 'affiliate_networks', action: 'list' });
  const feedAccess = useCanAccess({ resource: 'feeds', action: 'list' });
  const regionsAccess = useCanAccess({ resource: 'regions', action: 'list' });
  const registrationAccess = useCanAccess({ resource: 'registrations', action: 'list' });
  const canListAffiliateNetworks = affiliateNetworkAccess.canAccess ?? false;
  const canListFeeds = feedAccess.canAccess ?? false;
  const canListRegions = regionsAccess.canAccess ?? false;
  const canListRegistrations = registrationAccess.canAccess ?? false;

  return (
    <>
      <ImageUrlInput label="Logo" source="attributes.image_url" />
      <Labeled>
        <FunctionField
          label="Merchant Portal Link"
          render={(record?: Partial<MerchantResource>) => {
            const username = record?.attributes?.username;

            if (!username) {
              return <i>Unknown</i>;
            }

            return (
              <a
                href={`https://merchant.getprice.com.au/Login.aspx?u=${username}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {username}
              </a>
            );
          }}
        />
      </Labeled>
      <Divider flexItem />
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={{ xs: 12, xl: 6 }}>
          <Box flex={1}>
            <TextInput
              label="Company Name"
              source="attributes.company_name"
              validate={maxLength(200)}
            />
          </Box>
          <Box flex={1}>
            <TextInput
              label="Merchant Name"
              source="attributes.merchant_name"
              validate={[required(), maxLength(200)]}
            />
          </Box>
          <Box flex={1}>
            <TextInput label="Website" source="attributes.website" validate={maxLength(200)} />
          </Box>
          <NumberInput
            defaultValue={40}
            isRequired
            label="CPC"
            source="attributes.cpc"
            step={1}
            validate={required()}
          />
          <Box flex={1}>
            <TextInput
              label="Description"
              multiline
              source="attributes.description"
              validate={maxLength(1400)}
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 12, xl: 6 }}>
          <Box flex={1}>
            <JsonApiHasOneReferenceInput
              defaultValue="0"
              source="relationships.merchant_status"
              reference="merchant_statuses"
              optionText="attributes.name"
              label="Merchant Status"
              isRequired
              validate={required()}
            />
          </Box>
          <Box flex={1}>
            <TextInput
              label="Salesperson"
              source="attributes.salesperson"
              validate={maxLength(100)}
            />
          </Box>
          {canListRegistrations && (
            <Box flex={1}>
              <JsonApiHasOneReferenceInput<MerchantResource, 'registration'>
                emptyText="Record missing"
                filterFields={['merchant_name', 'company_name']}
                label="Registration"
                optionText={(choice: RegistrationResource) => {
                  let name = '';

                  if (choice.attributes.company_name) {
                    name += choice.attributes.company_name;
                    name += ' | ';
                  }

                  if (choice.attributes.merchant_name) {
                    name += choice.attributes.merchant_name;
                  }

                  name += ` (${choice.id})`;

                  return name;
                }}
                reference="registrations"
                source="relationships.registration"
              />
            </Box>
          )}
          {canListRegions && (
            <Box flex={1}>
              <JsonApiHasOneReferenceInput<MerchantResource, 'region'>
                defaultValue="1"
                isRequired
                validate={[required()]}
                label="Region"
                optionText="attributes.name"
                reference="regions"
                source="relationships.region"
              />
            </Box>
          )}
          <FormDataConsumer>
            {({ formData }) => {
              const merchantId = formData?.id as string | undefined;
              const billingTypeCode = formData?.attributes?.billing_type_code as number | undefined;
              const isCPAMerchant =
                billingTypeCode === BILLING_TYPE_CHOICES.find((v) => v.name === 'CPA')?.id;
              const feeds = formData?.relationships?.feeds?.data as
                { type: 'feeds'; id: string }[] | undefined;
              const hasFeeds = (feeds?.length ?? 0) > 0;

              return (
                <>
                  {canListAffiliateNetworks && isCPAMerchant && (
                    <Box flex={1}>
                      <JsonApiHasOneReferenceInput<MerchantResource, 'affiliate_network'>
                        emptyText="Record missing"
                        filterFields={['name', 'subtag_name']}
                        label="Affiliate Network"
                        optionText={(choice: AffiliateNetworkResource) =>
                          `${choice.attributes.name} - ${choice.attributes.subtag_name} (${choice.id})`
                        }
                        reference="affiliate_networks"
                        source="relationships.affiliate_network"
                      />
                    </Box>
                  )}
                  {canListFeeds && merchantId && (
                    <Box flex={1}>
                      <Labeled label="Feeds">
                        {hasFeeds ? (
                          <ReferenceManyField reference="feeds" target="merchant.id">
                            <SingleFieldList>
                              <ChipField source="id" />
                            </SingleFieldList>
                          </ReferenceManyField>
                        ) : (
                          <CreateButton
                            resource="feeds"
                            state={{
                              record: {
                                relationships: {
                                  merchant: { data: { type: 'merchants', id: merchantId } },
                                },
                              },
                            }}
                          />
                        )}
                      </Labeled>
                    </Box>
                  )}
                </>
              );
            }}
          </FormDataConsumer>
        </Grid>
      </Grid>
    </>
  );
}
